import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { formatINR } from "@/lib/roles";
import { Loader2, Map as MapIcon } from "lucide-react";
import "leaflet/dist/leaflet.css";

const TYPE_COLORS = {
  residential: "#0F2942",
  commercial: "#E8702A",
  industrial: "#7C3AED",
  mixed: "#0D7650",
};

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      const lats = points.map((p) => p.latitude);
      const lngs = points.map((p) => p.longitude);
      map.fitBounds([[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]], { padding: [30, 30] });
    }
  }, [points, map]);
  return null;
}

export default function GISMap() {
  const [items, setItems] = useState([]);
  const [wards, setWards] = useState([]);
  const [ward, setWard] = useState("all");
  const [ptype, setPtype] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/master/wards").then((r) => setWards(r.data.items || []));
    api.get("/properties/geo").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  const filtered = items.filter((p) => {
    if (ward !== "all" && p.ward !== ward) return false;
    if (ptype !== "all" && p.property_type !== ptype) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="gis-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">GIS</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
          <MapIcon className="w-6 h-6 text-[#0F2942]" /> Property GIS Map
        </h1>
        <p className="text-sm text-slate-500 mt-1">Punjab Municipal Corporation properties · {filtered.length} shown</p>
      </div>

      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <Select value={ward} onValueChange={setWard}>
            <SelectTrigger data-testid="gis-select-ward"><SelectValue placeholder="Ward" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All wards</SelectItem>
              {wards.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={ptype} onValueChange={setPtype}>
            <SelectTrigger data-testid="gis-select-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="residential">Residential</SelectItem>
              <SelectItem value="commercial">Commercial</SelectItem>
              <SelectItem value="industrial">Industrial</SelectItem>
              <SelectItem value="mixed">Mixed</SelectItem>
            </SelectContent>
          </Select>
          <div className="md:col-span-2 flex items-center gap-3 text-xs">
            <span className="text-slate-500 uppercase font-semibold tracking-wider">Legend:</span>
            {Object.entries(TYPE_COLORS).map(([k, c]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ background: c }} />
                <span className="capitalize">{k}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="h-[600px]" data-testid="gis-map">
              <MapContainer center={[30.9010, 75.8573]} zoom={13} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds points={filtered} />
                {filtered.map((p) => (
                  <CircleMarker
                    key={p.id}
                    center={[p.latitude, p.longitude]}
                    radius={7}
                    pathOptions={{ color: TYPE_COLORS[p.property_type] || "#0F2942", fillColor: TYPE_COLORS[p.property_type] || "#0F2942", fillOpacity: 0.7 }}
                  >
                    <Popup>
                      <div className="text-xs space-y-1">
                        <div className="font-semibold">{p.property_id}</div>
                        <div>{p.owner_name}</div>
                        <div className="text-slate-500">{p.address}</div>
                        <div><Badge variant="outline" className="capitalize">{p.property_type}</Badge> · {p.ward}</div>
                        {p.total_dues > 0 && <div className="text-red-600 font-semibold">Dues: {formatINR(p.total_dues)}</div>}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
